/**
 * LLM providers — the operator screen for the priced `llm_model` catalog.
 *
 * This is where a provider becomes usable, and the loop is deliberately visible on one page:
 *
 *   1. add a row here            -> it shows `key missing`
 *   2. set the env var named in `keyRef` in tool-service, redeploy it
 *   3. Refresh                   -> it shows `available`
 *
 * Step 2 cannot happen here and must not appear to: hub never holds an API key. The row
 * stores the NAME of an environment variable; the value exists only inside tool-service's
 * process. That separation is what makes spend the platform's rather than a passthrough, and
 * it is why this page replaced `/cloud` — that page asked hub which keys were set, an answer
 * hub is structurally unable to give correctly.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  hubService,
  type CreateLlmProviderDto,
  type OperatorLlmProvider,
  type ProviderStatus,
  type UpdateLlmProviderDto,
} from '../services/hub.service';

const STATUS_STYLE: Record<ProviderStatus, { cls: string; label: string; hint: string }> = {
  available: {
    cls: 'bg-green-100 text-green-800',
    label: 'available',
    hint: 'Active here, and its key exists in tool-service. Bots can be pointed at it.',
  },
  key_missing: {
    cls: 'bg-amber-100 text-amber-800',
    label: 'key missing',
    hint: 'Active here, but the environment variable is not set in tool-service. Set it there and redeploy.',
  },
  disabled: {
    cls: 'bg-gray-200 text-gray-700',
    label: 'disabled',
    hint: 'Withdrawn here. Bots already pointing at it fall back to the deployment default.',
  },
  unknown: {
    cls: 'bg-slate-100 text-slate-600',
    label: 'unknown',
    hint: 'tool-service could not be reached, so the key check did not run. This is not a "no".',
  },
};

const EMPTY_FORM = {
  id: '',
  vendor: '',
  model: '',
  label: '',
  inputPricePer1kMnt: '0',
  outputPricePer1kMnt: '0',
  inputCostPer1kUsd: '0',
  outputCostPer1kUsd: '0',
  keyRef: '',
  toolsCapable: false,
  isActive: true,
  isDefault: false,
};

type FormState = typeof EMPTY_FORM;

function toForm(row: OperatorLlmProvider): FormState {
  return {
    id: row.id,
    vendor: row.vendor,
    model: row.model,
    label: row.label,
    inputPricePer1kMnt: String(row.inputPricePer1kMnt ?? 0),
    outputPricePer1kMnt: String(row.outputPricePer1kMnt ?? 0),
    inputCostPer1kUsd: String(row.inputCostPer1kUsd ?? 0),
    outputCostPer1kUsd: String(row.outputCostPer1kUsd ?? 0),
    keyRef: row.keyRef ?? '',
    toolsCapable: row.toolsCapable,
    isActive: row.isActive,
    isDefault: row.isDefault,
  };
}

/** Blank stays blank rather than becoming 0 — an omitted price is not a free one. */
function num(v: string): number | undefined {
  const t = v.trim();
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

function fmtMnt(v: number) {
  return `₮${(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 4 })}`;
}

function fmtUsd(v: number) {
  const s = (v ?? 0).toFixed(6);
  return `$${s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s}`;
}

export function LlmProviders() {
  const { getToken } = useAuth();
  const [rows, setRows] = useState<OperatorLlmProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // `null` = form closed; '' = adding; an id = editing that row.
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await hubService.listLlmProviders(getToken()));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    load();
  }, [load]);

  const anyUnknown = useMemo(() => rows.some((r) => r.status === 'unknown'), [rows]);

  function startAdd() {
    setForm(EMPTY_FORM);
    setEditing('');
    setError(null);
  }

  function startEdit(row: OperatorLlmProvider) {
    setForm(toForm(row));
    setEditing(row.id);
    setError(null);
  }

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit() {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const token = getToken();
      const prices = {
        inputPricePer1kMnt: num(form.inputPricePer1kMnt),
        outputPricePer1kMnt: num(form.outputPricePer1kMnt),
        inputCostPer1kUsd: num(form.inputCostPer1kUsd),
        outputCostPer1kUsd: num(form.outputCostPer1kUsd),
      };
      if (editing === '') {
        const dto: CreateLlmProviderDto = {
          id: form.id.trim(),
          vendor: form.vendor.trim(),
          model: form.model.trim(),
          label: form.label.trim(),
          ...prices,
          // Omit when blank: '' would fail the env-var-NAME pattern on the server.
          ...(form.keyRef.trim() ? { keyRef: form.keyRef.trim() } : {}),
          toolsCapable: form.toolsCapable,
          isActive: form.isActive,
          isDefault: form.isDefault,
        };
        await hubService.createLlmProvider(dto, token);
        setNotice(`Added ${dto.id}.`);
      } else {
        const dto: UpdateLlmProviderDto = {
          model: form.model.trim(),
          label: form.label.trim(),
          ...prices,
          // null, not '' — it is the documented way to clear the override and return the row
          // to its vendor's default key_ref.
          keyRef: form.keyRef.trim() || null,
          toolsCapable: form.toolsCapable,
          isActive: form.isActive,
          isDefault: form.isDefault,
        };
        await hubService.updateLlmProvider(editing!, dto, token);
        setNotice(`Saved ${editing}.`);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(row: OperatorLlmProvider) {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await hubService.updateLlmProvider(row.id, { isActive: !row.isActive }, getToken());
      setNotice(`${row.id} ${row.isActive ? 'withdrawn' : 're-enabled'}.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const isAdd = editing === '';

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
        <div className="mb-2 flex items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">LLM providers</h1>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={load}
              disabled={loading || saving}
              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={startAdd}
              disabled={saving}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-40"
            >
              Add provider
            </button>
          </div>
        </div>

        <p className="mb-6 text-sm text-gray-600">
          The priced catalog a bot can be pointed at, from its Config → LLM tab. A row is usable
          only when it is active here <em>and</em> its API key exists in tool-service&apos;s
          environment. Hub stores the <strong>name</strong> of that environment variable, never
          the key itself.
        </p>

        {error && (
          <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        {notice && !error && (
          <div className="mb-4 rounded border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">
            {notice}
          </div>
        )}
        {anyUnknown && (
          <div className="mb-4 rounded border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-700">
            Some rows show <strong>unknown</strong>: tool-service could not be reached, so the key
            check did not run. Prices and switches on this page still work — only the status
            column is affected.
          </div>
        )}

        {editing !== null && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">
              {isAdd ? 'Add provider' : `Edit ${editing}`}
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="id" hint="vendor:model, e.g. openai:gpt-4o">
                <input
                  value={form.id}
                  disabled={!isAdd}
                  onChange={(e) => set('id', e.target.value)}
                  placeholder="openai:gpt-4o"
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                />
              </Field>
              <Field
                label="vendor"
                hint={
                  isAdd
                    ? 'Billing identity — checked against tool-service'
                    : 'Immutable: it is the row’s billing identity'
                }
              >
                <input
                  value={form.vendor}
                  disabled={!isAdd}
                  onChange={(e) => set('vendor', e.target.value)}
                  placeholder="openai"
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                />
              </Field>
              <Field label="model" hint="As the vendor's API expects it">
                <input
                  value={form.model}
                  onChange={(e) => set('model', e.target.value)}
                  placeholder="gpt-4o"
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                />
              </Field>
              <Field label="label" hint="Shown to bot owners in the picker">
                <input
                  value={form.label}
                  onChange={(e) => set('label', e.target.value)}
                  placeholder="GPT-4o"
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                />
              </Field>
              <Field
                label="keyRef"
                hint="Env var NAME in tool-service — never a key. Blank = vendor default."
              >
                <input
                  value={form.keyRef}
                  onChange={(e) => set('keyRef', e.target.value)}
                  placeholder="DEBUG_OPENAI_KEY"
                  className="w-full rounded border border-gray-300 px-2 py-1.5 font-mono text-sm"
                />
              </Field>
              <Field label="flags">
                <div className="flex flex-wrap gap-4 pt-1.5 text-sm text-gray-700">
                  <Check
                    checked={form.toolsCapable}
                    onChange={(v) => set('toolsCapable', v)}
                    label="tools capable"
                  />
                  <Check checked={form.isActive} onChange={(v) => set('isActive', v)} label="active" />
                  <Check
                    checked={form.isDefault}
                    onChange={(v) => set('isDefault', v)}
                    label="default"
                  />
                </div>
              </Field>
              <Field label="₮ in / 1k" hint="Charged to the bot owner">
                <input
                  value={form.inputPricePer1kMnt}
                  onChange={(e) => set('inputPricePer1kMnt', e.target.value)}
                  inputMode="decimal"
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                />
              </Field>
              <Field label="₮ out / 1k" hint="Charged to the bot owner">
                <input
                  value={form.outputPricePer1kMnt}
                  onChange={(e) => set('outputPricePer1kMnt', e.target.value)}
                  inputMode="decimal"
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                />
              </Field>
              <div className="hidden lg:block" />
              <Field label="$ in / 1k" hint="What the vendor bills us — never charged on">
                <input
                  value={form.inputCostPer1kUsd}
                  onChange={(e) => set('inputCostPer1kUsd', e.target.value)}
                  inputMode="decimal"
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                />
              </Field>
              <Field label="$ out / 1k" hint="What the vendor bills us — never charged on">
                <input
                  value={form.outputCostPer1kUsd}
                  onChange={(e) => set('outputCostPer1kUsd', e.target.value)}
                  inputMode="decimal"
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                />
              </Field>
            </div>
            <div className="mt-5 flex items-center gap-2">
              <button
                type="button"
                onClick={submit}
                disabled={
                  saving ||
                  !form.model.trim() ||
                  !form.label.trim() ||
                  (isAdd && (!form.id.trim() || !form.vendor.trim()))
                }
                className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-40"
              >
                {saving ? 'Saving…' : isAdd ? 'Create' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setEditing(null)}
                disabled={saving}
                className="rounded border border-gray-300 px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading && <div className="text-gray-500">Loading…</div>}

        {!loading && rows.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
            No providers yet. Bots with no model selected run on tool-service&apos;s deployment
            default, which is unaffected by this page.
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-2 font-semibold">Provider</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                  <th className="px-4 py-2 text-right font-semibold">₮ in / out</th>
                  <th className="px-4 py-2 text-right font-semibold">$ cost in / out</th>
                  <th className="px-4 py-2 font-semibold">Key</th>
                  <th className="px-4 py-2 font-semibold">Tools</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const s = STATUS_STYLE[r.status] ?? STATUS_STYLE.unknown;
                  return (
                    <tr key={r.id} className="border-b border-gray-100 align-top last:border-0">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {r.label}
                          {r.isDefault && (
                            <span className="ml-2 rounded bg-blue-50 px-1.5 py-0.5 text-[11px] font-normal text-blue-700">
                              default
                            </span>
                          )}
                        </div>
                        <div className="font-mono text-xs text-gray-500">{r.id}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded px-2 py-0.5 text-xs ${s.cls}`} title={s.hint}>
                          {s.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                        {fmtMnt(r.inputPricePer1kMnt)} / {fmtMnt(r.outputPricePer1kMnt)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-500">
                        {fmtUsd(r.inputCostPer1kUsd)} / {fmtUsd(r.outputCostPer1kUsd)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">
                        {r.effectiveKeyRef ?? (
                          <span className="font-sans text-gray-400">vendor default</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{r.toolsCapable ? 'yes' : 'no'}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => startEdit(r)}
                            disabled={saving}
                            className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-40"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleActive(r)}
                            disabled={saving}
                            title={
                              r.isActive
                                ? 'Withdraw from the picker. Rows are never deleted — usage history joins against this price.'
                                : 'Offer this model in the bot picker again.'
                            }
                            className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-40"
                          >
                            {r.isActive ? 'Withdraw' : 'Enable'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-6 text-xs text-gray-500">
          Rows are withdrawn, never deleted: a deleted row would orphan the price that usage
          history and billing join against. A bot pointing at a withdrawn row keeps serving on
          the deployment default rather than failing.
        </p>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

function Check({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-1.5">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
